import { default as React, useCallback, useState } from 'react';

import { RouteProp, useNavigation, useRoute } from '@react-navigation/core';
import { StackActions, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import ActivityIndicatorScreen from '../../components/ActivityIndicatorScreen';
import { useUserContext } from '../../context/UserContext';
import DynamicForm from '../../dynamic-forms/DynamicForm';
import DynamicFormProvider from '../../dynamic-forms/DynamicFormProvider';
import FormDataSource from '../../dynamic-forms/FormDataSource';
import { useGetFormTemplate } from '../../dynamic-forms/hooks/useGetFormTemplate';
import { useRunQuery } from '../../dynamic-forms/hooks/useRunQuery';
import { WelcomeNavigatorParamList } from '../../navigation/WelcomeNavigator';
import styled from 'styled-components/native';
import Colors from '../../constants/Colors';
import { Platform } from 'react-native';
import { log } from '../../tools/LogHelper';
import { stringify } from 'flatted';

const StyledViewContainer = styled.View`
  height: 100%;
  background: ${Colors.background};
`;

const WelcomeSurveyScreen: React.FC = () => {
  const { user } = useUserContext();
  const navigation =
    useNavigation<StackNavigationProp<WelcomeNavigatorParamList>>();
  const route =
    useRoute<RouteProp<WelcomeNavigatorParamList, 'WelcomeSurvey'>>();
  const path = route.params?.path;
  const history: string[] = route.params?.history
    ? JSON.parse(route.params.history)
    : [];
  // const currentIndex = route.params?.sectionIndex || 0;
  const { formTemplate, loadingFormTemplate, getFormTemplate } =
    useGetFormTemplate('Onboarding Form');
  useFocusEffect(useCallback(() => getFormTemplate(), [getFormTemplate]));

  // run query to get entity data
  const { queryData, runQueryGetOne } = useRunQuery();
  useFocusEffect(
    useCallback(() => {
      if (user?.id) {
        runQueryGetOne({
          queryType: 'select',
          selects: [],
          mainAlias: {
            type: 'from',
            name: 'onboardingForm',
            tablePath: 'onboarding_form'
          },
          wheres: [
            {
              type: 'simple',
              condition: {
                operator: 'equal',
                parameters: ['user_id', user.id.toString()]
              }
            }
          ]
        });
      }
    }, [user, runQueryGetOne])
  );

  // display spinner while loading elements to prevent flickering
  const [loadingElements, setLoadingElements] = useState(false);

  // console.log('navigation', navigation.getState());
  // console.log('queryData', queryData);
  return formTemplate && !loadingFormTemplate ? (
    <StyledViewContainer>
      <DynamicFormProvider
        entitySchema={formTemplate?.entity}
        formStateNode={formTemplate?.config}
        onLoadProgress={loadingElementQueue => {
          setLoadingElements(loadingElementQueue.length > 0);
        }}
      >
        <FormDataSource formTemplate={formTemplate} formData={queryData}>
          <DynamicForm
            values={queryData}
            statePath={path}
            onBack={() => {
              log('onBack navigation', stringify(navigation.getState()));
              navigation.pop();
            }}
            onStatePathChange={(
              path,
              stateNode,
              sectionIndex,
              sectionCount
            ) => {
              // console.log('onStatePathChange', path);
              const currentIndex = route.params?.sectionIndex || 0;
              if (
                sectionIndex != 0 &&
                sectionIndex >= currentIndex &&
                sectionIndex < sectionCount
              ) {
                history.push(path);
                // console.log('push navigation', history);
                navigation.push('WelcomeSurvey', {
                  path,
                  sectionIndex: sectionIndex + 1,
                  history: JSON.stringify(history)
                });
              } else if (sectionIndex >= sectionCount) {
                navigation.dispatch(
                  StackActions.replace('DrawerNavigator', {
                    screen: 'BottomTabNav'
                  })
                );
              }
            }}
          />
        </FormDataSource>
        {loadingElements && (
          <ActivityIndicatorScreen modal backgroundColor='#fff' />
        )}
      </DynamicFormProvider>
    </StyledViewContainer>
  ) : (
    <ActivityIndicatorScreen />
  );
};

export default WelcomeSurveyScreen;
